import SignaturePad from "signature_pad";

/** Copy existing pixels, never redraw/rescale strokes for the signed artifact. */
function exportSignaturePng(canvas: HTMLCanvasElement, pad: SignaturePad, ratio: number): string {
  const fallback = () => pad.toDataURL("image/png");
  if (!canvas.width || !canvas.height || !Number.isFinite(ratio) || ratio <= 0) return fallback();
  let left = Infinity; let top = Infinity; let right = -Infinity; let bottom = -Infinity;
  for (const group of pad.toData()) {
    // SignaturePad widths/dotSize are radii in CSS pixels, not diameters.
    const radius = Math.max(0, group.minWidth, group.maxWidth, group.dotSize);
    if (!Number.isFinite(radius)) return fallback();
    for (const point of group.points) {
      if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return fallback();
      left = Math.min(left, (point.x - radius) * ratio);
      top = Math.min(top, (point.y - radius) * ratio);
      right = Math.max(right, (point.x + radius) * ratio);
      bottom = Math.max(bottom, (point.y + radius) * ratio);
    }
  }
  if (!Number.isFinite(left)) return fallback();
  try {
    const source = canvas.getContext("2d");
    if (!source) return fallback();
    const pixels = source.getImageData(0, 0, canvas.width, canvas.height);
    let hasInk = false;
    // Curves can extend beyond sampled points. Include every nontransparent
    // pixel so trimming cannot clip Bézier overshoot or antialiased edges.
    for (let y = 0; y < canvas.height; y += 1) {
      for (let x = 0; x < canvas.width; x += 1) {
        if (!pixels.data[(y * canvas.width + x) * 4 + 3]) continue;
        hasInk = true;
        left = Math.min(left, x); top = Math.min(top, y);
        right = Math.max(right, x + 1); bottom = Math.max(bottom, y + 1);
      }
    }
    if (!hasInk) return fallback();
    const padding = 4 * ratio;
    const x = Math.max(0, Math.floor(left - padding));
    const y = Math.max(0, Math.floor(top - padding));
    const endX = Math.min(canvas.width, Math.ceil(right + padding));
    const endY = Math.min(canvas.height, Math.ceil(bottom + padding));
    if (endX <= x || endY <= y) return fallback();
    const cropped = document.createElement("canvas");
    cropped.width = endX - x; cropped.height = endY - y;
    const destination = cropped.getContext("2d");
    if (!destination) return fallback();
    destination.putImageData(pixels, -x, -y);
    return cropped.toDataURL("image/png");
  } catch {
    // Preserve the original export if pixel readback is unavailable.
    return fallback();
  }
}

/** Keep vector strokes through layout changes, without stretching the signature. */
export function createSignature(canvas: HTMLCanvasElement): {
  pad: SignaturePad;
  resize: () => void;
  exportPng: () => string;
  dispose: () => void;
} {
  const pad = new SignaturePad(canvas, { penColor: "#0f172a" });
  let width = 0;
  let height = 0;
  let pixelRatio = 0;
  const resize = (): void => {
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    if (width === rect.width && height === rect.height && ratio === pixelRatio) return;
    const strokes = pad.toData();
    const scale = width && height ? Math.min(rect.width / width, rect.height / height) : 1;
    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);
    canvas.getContext("2d")?.scale(ratio, ratio);
    pad.clear();
    if (strokes.length) {
      pad.fromData(strokes.map((group) => ({
        ...group,
        minWidth: group.minWidth * scale,
        maxWidth: group.maxWidth * scale,
        dotSize: group.dotSize * scale,
        points: group.points.map((point) => ({ ...point, x: point.x * scale, y: point.y * scale })),
      })));
    }
    width = rect.width;
    height = rect.height;
    pixelRatio = ratio;
  };
  resize();
  window.addEventListener("resize", resize);
  return { pad, resize, exportPng: () => exportSignaturePng(canvas, pad, pixelRatio),
    dispose: () => { window.removeEventListener("resize", resize); pad.off(); } };
}
